<template>
  <app-layout>
    <div class="sc-container rc-page">
      <div class="rc-card">
        <div class="rc-card__header">
          <h1 class="rc-title">Register a Company</h1>
          <p class="rc-sub">Create your company profile to post projects, properties, and concepts under your brand.</p>
        </div>

        <div v-if="success" class="rc-success">
          <svg width="48" height="48" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="12" fill="#059669"/><path d="M7 12l3.5 3.5L17 8" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <h2>Company registered!</h2>
          <p>Your company <strong>{{ newCompany.name }}</strong> has been created.</p>
          <router-link :to="'/company/'+newCompany.slug" class="rc-btn rc-btn--primary">View Company Page</router-link>
        </div>

        <form v-else class="rc-form" @submit.prevent="submit">
          <!-- Name -->
          <div class="rc-field">
            <label class="rc-label">Company Name <span class="req">*</span></label>
            <input type="text" v-model="form.name" class="rc-input" placeholder="e.g. ABC Construction Ltd" required />
          </div>

          <!-- Industry -->
          <div class="rc-field">
            <label class="rc-label">Industry</label>
            <select v-model="form.industry_id" class="rc-input">
              <option value="">-- select industry --</option>
              <option v-for="ind in industries" :key="ind.id" :value="ind.id">{{ ind.name }}</option>
            </select>
          </div>

          <!-- Company Size -->
          <div class="rc-field">
            <label class="rc-label">Company Size</label>
            <select v-model="form.company_size" class="rc-input">
              <option value="">-- select size --</option>
              <option value="1-10">1–10 employees</option>
              <option value="11-25">11–25 employees</option>
              <option value="25-50">25–50 employees</option>
              <option value="50-100">50–100 employees</option>
              <option value="100-500">100–500 employees</option>
              <option value="500-1000">500–1000 employees</option>
              <option value="1000-10000">1000–10,000 employees</option>
              <option value="10000-50000">10,000–50,000 employees</option>
            </select>
          </div>

          <!-- Description -->
          <div class="rc-field">
            <label class="rc-label">Company Description</label>
            <textarea v-model="form.short_description" class="rc-input rc-textarea" rows="4" placeholder="Brief overview of what your company does…"></textarea>
          </div>

          <!-- Email -->
          <div class="rc-field">
            <label class="rc-label">Contact Email <span class="req">*</span></label>
            <input type="email" v-model="form.email" class="rc-input" placeholder="company@example.com" required />
          </div>

          <!-- Phone -->
          <div class="rc-row">
            <div class="rc-field">
              <label class="rc-label">Phone Number <span class="req">*</span></label>
              <input type="tel" v-model="form.phone_number" class="rc-input" placeholder="+234…" required />
            </div>
            <div class="rc-field">
              <label class="rc-label">Phone Number 2</label>
              <input type="tel" v-model="form.phone_number2" class="rc-input" placeholder="+234…" />
            </div>
          </div>

          <!-- Website -->
          <div class="rc-field">
            <label class="rc-label">Website</label>
            <input type="url" v-model="form.website" class="rc-input" placeholder="https://…" />
          </div>

          <!-- Location -->
          <p class="rc-section-label">Company Location</p>
          <div class="rc-row">
            <div class="rc-field">
              <label class="rc-label">Country</label>
              <select v-model="form.country_id" class="rc-input" @change="onCountryChange">
                <option value="">Country</option>
                <option v-for="c in countries" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
            <div class="rc-field">
              <label class="rc-label">State</label>
              <select v-model="form.state_id" class="rc-input" @change="onStateChange">
                <option value="">State</option>
                <option v-for="s in states" :key="s.id" :value="s.id">{{ s.name }}</option>
              </select>
            </div>
            <div class="rc-field">
              <label class="rc-label">City</label>
              <select v-model="form.city_id" class="rc-input">
                <option value="">City</option>
                <option v-for="c in cities" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
          </div>

          <!-- Address -->
          <div class="rc-field">
            <label class="rc-label">Address</label>
            <input type="text" v-model="form.address" class="rc-input" placeholder="Street address…" />
          </div>

          <!-- Logo -->
          <div class="rc-field">
            <label class="rc-label">Company Logo</label>
            <div class="rc-logo-upload">
              <img v-if="logoPreview" :src="logoPreview" class="rc-logo-preview" alt="Logo preview" />
              <div v-else class="rc-logo-placeholder">
                <svg width="32" height="32" fill="none" viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" stroke="currentColor" stroke-width="1.5"/></svg>
                <span>Logo</span>
              </div>
              <div class="rc-logo-btn-wrap">
                <label class="rc-btn rc-btn--outline rc-upload-label" for="logo-upload">
                  {{ logoPreview ? 'Change Logo' : 'Upload Logo' }}
                </label>
                <input id="logo-upload" type="file" accept="image/*" @change="onLogoChange" class="rc-file-hidden" />
                <p class="rc-hint">PNG, JPG up to 2 MB. Recommended 200×200px.</p>
              </div>
            </div>
          </div>

          <!-- Error -->
          <div v-if="error" class="rc-error">{{ error }}</div>

          <!-- Submit -->
          <div class="rc-submit-row">
            <button type="submit" class="rc-btn rc-btn--primary" :disabled="loading">
              <span v-if="loading" class="rc-spinner"></span>
              <span v-else>Register Company</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </app-layout>
</template>

<script>
import AppLayout from '@/layouts/AppLayout';
import axios from 'axios';

export default {
  name: 'RegisterCompany',
  components: { AppLayout },

  data() {
    return {
      loading: false,
      success: false,
      error: '',
      newCompany: {},
      logoFile: null,
      logoPreview: '',
      industries: [],
      countries: [],
      states: [],
      cities: [],
      form: {
        name: '',
        industry_id: '',
        company_size: '',
        short_description: '',
        email: '',
        phone_number: '',
        phone_number2: '',
        website: '',
        country_id: '',
        state_id: '',
        city_id: '',
        address: '',
      },
    };
  },

  created() {
    this.fetchIndustries();
    this.fetchCountries();
    this.fetchNigeriaStates();
  },

  methods: {
    fetchIndustries() {
      axios(process.env.VUE_APP_API_URL + 'fetch-industries').then(res => {
        if (res.data) this.industries = res.data;
      }).catch(() => {});
    },

    fetchCountries() {
      axios(process.env.VUE_APP_API_URL + 'country').then(res => {
        if (res.data) this.countries = res.data;
      }).catch(() => {});
    },

    fetchNigeriaStates() {
      axios(process.env.VUE_APP_API_URL + 'state/160').then(res => {
        if (res.data) this.states = res.data;
      }).catch(() => {});
    },

    onCountryChange() {
      this.form.state_id = '';
      this.form.city_id = '';
      this.states = [];
      this.cities = [];
      if (!this.form.country_id) return;
      axios(process.env.VUE_APP_API_URL + 'state/' + this.form.country_id).then(res => {
        if (res.data) this.states = res.data;
      }).catch(() => {});
    },

    onStateChange() {
      this.form.city_id = '';
      this.cities = [];
      if (!this.form.state_id) return;
      axios(process.env.VUE_APP_API_URL + 'city/' + this.form.state_id).then(res => {
        if (res.data) this.cities = res.data;
      }).catch(() => {});
    },

    onLogoChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.logoFile = file;
      const reader = new FileReader();
      reader.onload = ev => { this.logoPreview = ev.target.result; };
      reader.readAsDataURL(file);
    },

    async submit() {
      this.error = '';
      this.loading = true;
      const fd = new FormData();
      Object.keys(this.form).forEach(k => {
        if (this.form[k] !== '') fd.append(k, this.form[k]);
      });
      if (this.logoFile) fd.append('logo', this.logoFile);

      try {
        const res = await axios.post(process.env.VUE_APP_API_URL + 'user/create-company', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (res.status === 200 && (res.data.data || res.data.slug || res.data.id)) {
          this.newCompany = res.data.data || res.data;
          this.success = true;
        } else {
          this.error = res.data?.message || 'Registration failed. Please try again.';
        }
      } catch (err) {
        const data = err.response?.data;
        if (data?.errors) {
          this.error = Object.values(data.errors).flat().join(' ');
        } else if (data?.message) {
          this.error = Array.isArray(data.message) ? data.message.join(' ') : data.message;
        } else {
          this.error = 'Registration failed. Please try again.';
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.rc-page { padding: 40px 16px 64px; }

.rc-card {
  max-width: 680px;
  margin: 0 auto;
  background: var(--surface, #fff);
  border: 1px solid var(--border-light, #e5e7eb);
  border-radius: var(--radius-lg, 12px);
  box-shadow: var(--shadow, 0 1px 6px rgba(0,0,0,.08));
  padding: 36px 40px;
}
@media (max-width: 600px) { .rc-card { padding: 24px 16px; } }

.rc-card__header { margin-bottom: 28px; }
.rc-title { font-size: 1.5rem; font-weight: 700; color: var(--text, #111827); margin: 0 0 6px; }
.rc-sub { color: var(--text-muted, #6b7280); font-size: .95rem; margin: 0; }

/* Form */
.rc-form { display: flex; flex-direction: column; gap: 20px; }
.rc-section-label { font-weight: 600; font-size: .9rem; color: var(--text, #374151); margin: 0; }
.rc-field { display: flex; flex-direction: column; gap: 6px; }
.rc-row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; }
@media (max-width: 600px) { .rc-row { grid-template-columns: 1fr; } }
.rc-label { font-size: .875rem; font-weight: 500; color: var(--text, #374151); }
.req { color: #ef4444; }
.rc-input {
  width: 100%; padding: 10px 14px;
  border: 1.5px solid var(--border-light, #e5e7eb);
  border-radius: 8px; font-size: .9rem;
  background: #fff; color: var(--text, #111827);
  outline: none; transition: border-color .18s;
  font-family: inherit; box-sizing: border-box;
}
.rc-input:focus { border-color: var(--primary, #2563eb); }
.rc-textarea { resize: vertical; min-height: 90px; }

/* Logo upload */
.rc-logo-upload { display: flex; align-items: center; gap: 16px; }
.rc-logo-preview { width: 80px; height: 80px; border-radius: 10px; object-fit: cover; border: 2px solid var(--border-light, #e5e7eb); }
.rc-logo-placeholder {
  width: 80px; height: 80px; border-radius: 10px;
  border: 2px dashed var(--border-light, #d1d5db);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  color: var(--text-muted, #9ca3af); font-size: .72rem; gap: 4px;
}
.rc-logo-btn-wrap { display: flex; flex-direction: column; gap: 6px; }
.rc-upload-label { cursor: pointer; display: inline-flex; }
.rc-file-hidden { display: none; }
.rc-hint { font-size: .78rem; color: var(--text-muted, #9ca3af); margin: 0; }

/* Error / success */
.rc-error {
  background: #fef2f2; border: 1px solid #fecaca;
  color: #dc2626; border-radius: 8px;
  padding: 10px 14px; font-size: .875rem;
}
.rc-success {
  text-align: center; padding: 32px 0;
  display: flex; flex-direction: column; align-items: center; gap: 12px;
}
.rc-success h2 { font-size: 1.3rem; font-weight: 700; margin: 0; color: var(--text, #111827); }
.rc-success p { color: var(--text-muted, #6b7280); margin: 0; }

/* Buttons */
.rc-submit-row { display: flex; justify-content: flex-end; }
.rc-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 24px; border-radius: 8px;
  font-size: .9rem; font-weight: 600; cursor: pointer;
  border: none; text-decoration: none; transition: all .18s;
}
.rc-btn--primary { background: var(--primary, #2563eb); color: #fff; }
.rc-btn--primary:hover:not(:disabled) { filter: brightness(1.1); }
.rc-btn--primary:disabled { opacity: .6; cursor: not-allowed; }
.rc-btn--outline {
  background: transparent;
  border: 1.5px solid var(--border-light, #e5e7eb);
  color: var(--text, #111827);
}
.rc-btn--outline:hover { background: #f3f4f6; }
.rc-spinner {
  width: 16px; height: 16px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,.4);
  border-top-color: #fff;
  animation: spin .7s linear infinite;
  display: inline-block;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
